import {
  Component,
  Input,
  ChangeDetectionStrategy,
  Inject,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Lesson } from '../../shared/models';
import { ImageLightboxComponent } from './image-lightbox/image-lightbox.component';

/**
 * Renders lesson content with rich media support (Markdown, video, images).
 */
@Component({
  selector: 'ut-lesson-display',
  templateUrl: './lesson-display.component.html',
  styleUrls: ['./lesson-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LessonDisplayComponent {
  @Input() lesson: Lesson | null = null;
  @ViewChild(ImageLightboxComponent) private lightbox!: ImageLightboxComponent;

  private htmlCache = new Map<string, SafeHtml>();

  constructor(
    private sanitizer: DomSanitizer,
    @Inject('$filter') private $filter: any,
  ) {}

  getSafeHtml(content: string): SafeHtml {
    if (!content) return '';

    const cached = this.htmlCache.get(content);
    if (cached) return cached;

    // Convert markdown images to expandable button-wrapped <img> tags.
    // Native <button> provides keyboard focus/activation for free (WCAG 2.1.1).
    let htmlContent = content.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      (_, alt, src) => {
        const label = alt ? `Expand image: ${alt}` : 'Expand image';
        return `<button type="button" class="lesson-image-button" aria-label="${label}"><img src="${src}" alt="${alt}"></button>`;
      },
    );

    if (this.$filter && this.$filter('uMarkdown')) {
      htmlContent = this.$filter('uMarkdown')(htmlContent);
    }

    // Bypass security to allow HTML content (matches AngularJS | trustAsHtml)
    // Content is from backend Markdown, not user input
    const result = this.sanitizer.bypassSecurityTrustHtml(htmlContent);
    this.htmlCache.set(content, result);
    return result;
  }

  onContentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const button = target.closest('.lesson-image-button') as HTMLElement | null;
    if (button) {
      const img = button.querySelector('img') as HTMLImageElement;
      if (img?.src) {
        this.lightbox.open(img.src, img.alt, button);
      }
    } else if (target.tagName === 'IMG') {
      const img = target as HTMLImageElement;
      if (img.src) {
        this.lightbox.open(img.src, img.alt);
      }
    }
  }

  get hasDescription(): boolean {
    return !!this.lesson?.description;
  }

  get hasSections(): boolean {
    return !!this.lesson?.sections && this.lesson.sections.length > 0;
  }
}
